<template>
  <!-- 任务看板：按状态泳道展示（未开始/进行中/已完成，已取消不展示） -->
  <div class="task-board">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="任务编号/标题" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.projectId" placeholder="全部项目" clearable filterable @change="onProjectFilter">
            <template #prefix>所属项目</template>
            <el-option v-for="p in projectOptions" :key="p.id" :label="p.projectName" :value="p.id"/>
          </el-select>
          <el-select v-model="searchInfo.sprintId" placeholder="全部批次" clearable filterable @change="resetSearch">
            <template #prefix>所属批次</template>
            <el-option v-for="s in sprintOptions" :key="s.id" :label="s.sprintName" :value="s.id"/>
          </el-select>
          <employee-select v-model="searchInfo.ownerId" placeholder="负责人（全部）">
            <template #prefix>负责人</template>
          </employee-select>
          <employee-select-multi v-model="searchInfo.testerStr" placeholder="选择测试人员（可多选）"/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openTaskDialog()">+ 新建任务</page-button>
        </template>
      </search-condition>
      <div class="board-body">
        <div v-for="col in columns" :key="col.key" class="board-column" :class="['col-' + col.key]">
          <div class="board-column-head">
            <span class="head-dot" :style="{background: col.color}"/>
            <span class="head-title">{{ col.label }}</span>
            <span class="head-count">{{ colList(col.key).length }}</span>
          </div>
          <div class="board-cards">
            <div v-for="task in colList(col.key)" :key="task.id" class="task-card">
              <div class="card-top">
                <div class="card-title" @click="openTaskDetail(task)">
                  {{ task.taskName || '未命名任务' }}
                  <span v-if="task.delayState && task.delayState !== '正常'" class="delay-flag">
                    {{ task.delayState === '延期中' ? '延期' + (task.delayDays || '') + '天' : '延期完成' }}
                  </span>
                </div>
                <div class="card-options">
                  <table-button @click="openTaskDialog(task)">编辑</table-button>
                </div>
              </div>
              <div class="card-sub">
                <span class="code-text">{{ task.taskCode || '—' }}</span>
                <span class="code-text">所属：{{ task.projectCode || '' }}{{ task.sprintName ? ' / ' + task.sprintName : '' }}</span>
              </div>
              <div class="card-meta">
                <span v-if="task.ownerName">负责人：{{ task.ownerName }}</span>
                <span v-if="task.planStart || task.planEnd">计划：{{ dateOnly(task.planStart) }} ~ {{ dateOnly(task.planEnd) }}</span>
                <span v-if="task.estimate != null">预估：{{ task.estimate }}人天</span>
              </div>
              <div class="card-progress">
                <el-progress :percentage="task.progress || 0" :stroke-width="8" :show-text="false"/>
                <span class="progress-text">{{ task.progress || 0 }}%</span>
              </div>
              <div class="card-actions">
                <template v-if="task.taskStatus === taskStatusCodes.TODO">
                  <table-button el-type="success" message="开始后将进入「进行中」，是否开始该任务？" @click="updateTaskStatus(task, taskStatusCodes.DOING)">开始</table-button>
                  <table-button message="填报节点进度（填完将自动转为进行中）" @click="openProgressDialog(task)">填报进度</table-button>
                  <table-button @click="openTaskDetail(task)">详情</table-button>
                  <table-button el-type="danger" message="是否取消该任务？" @click="updateTaskStatus(task, taskStatusCodes.CANCEL)">取消</table-button>
                </template>
                <template v-else-if="task.taskStatus === taskStatusCodes.DOING">
                  <table-button @click="openProgressDialog(task)">填报进度</table-button>
                  <table-button el-type="success" message="是否确认将该任务标记为完成？" @click="confirmDone(task)">标记完成</table-button>
                  <table-button el-type="danger" message="是否取消该任务？" @click="updateTaskStatus(task, taskStatusCodes.CANCEL)">取消</table-button>
                </template>
                <span v-else-if="task.taskStatus === taskStatusCodes.DONE" class="done-time">完成于 {{ dateTime(task.actualEnd) }}</span>
              </div>
            </div>
            <div v-if="!colList(col.key).length" class="column-empty">暂无任务</div>
          </div>
        </div>
      </div>
    </list-page>

    <!-- 新建/编辑任务弹窗（基本信息 + 关键节点） -->
    <el-dialog v-model="showTaskDialog" :title="dialogData.id ? '编辑任务' : '新建任务'" dialog-width="3" :close-on-click-modal="false" class="task-dialog">
      <div class="form-section">
        <div class="form-section-title">基本信息</div>
      </div>
      <form-items :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0" ref="taskForm">
        <form-item label="任务标题" :required="true" name="taskName" error-message="请填写任务标题" :show-block="true">
          <el-input v-model="dialogData.taskName" placeholder="任务标题"/>
        </form-item>
        <form-item label="所属项目" :required="true" name="projectId" error-message="请选择所属项目">
          <el-select v-model="dialogData.projectId" placeholder="请选择所属项目" filterable style="width: 100%" @change="onProjectChange">
            <el-option v-for="p in projectOptions" :key="p.id" :label="p.projectName" :value="p.id"/>
          </el-select>
        </form-item>
        <form-item label="所属批次">
          <el-select v-model="dialogData.sprintId" placeholder="可不挂批次" clearable filterable style="width: 100%">
            <el-option v-for="s in sprintOptions" :key="s.id" :label="s.sprintName" :value="s.id"/>
          </el-select>
        </form-item>
        <form-item label="负责人" :required="true" name="ownerId" error-message="请选择任务负责人">
          <employee-select v-model="dialogData.ownerId" placeholder="请选择负责人" @change="onOwnerChange"/>
        </form-item>
        <form-item label="工时估算">
          <el-input-number v-model="dialogData.estimate" :min="0" :max="999" :step="0.5" :precision="1" controls-position="right" style="width: 100%"/>
        </form-item>
        <form-item label="计划开始">
          <el-date-picker v-model="dialogData.planStart" type="date" value-format="YYYY-MM-DD" placeholder="节点可自动顺排" style="width: 100%"/>
        </form-item>
        <form-item label="计划完成">
          <el-date-picker v-model="dialogData.planEnd" type="date" value-format="YYYY-MM-DD" placeholder="留空取末节点结束日" style="width: 100%"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="备注信息"/>
        </form-item>
      </form-items>

      <div class="node-section">
        <div class="node-section-head">
          <span class="node-section-title">关键节点</span>
          <span class="muted">未填日期的节点将按任务计划开始日自动顺排（跳过周末），也可手动指定</span>
        </div>
        <div v-if="!dialogData.nodes.length" class="node-empty">还没有关键节点，点击下方「添加节点」新增</div>
        <div v-for="(node, idx) in dialogData.nodes" :key="idx" class="node-block">
          <div class="node-block-head">
            <span class="node-badge">{{ idx + 1 }}</span>
            <strong class="node-title">节点 {{ idx + 1 }}</strong>
            <span v-if="dialogData.id && node.pct != null" class="node-status-tip">进度 {{ node.pct }}%</span>
            <span class="flex-1"/>
            <table-button el-type="danger" size="small" @click="removeNode(idx)">删除节点</table-button>
          </div>
          <div class="node-grid">
            <div class="node-cell span-2">
              <span class="cell-label">节点名称</span>
              <el-input v-model="node.nodeName" placeholder="如：需求评审 / 接口开发 / 联调 / 测试验收"/>
            </div>
            <div class="node-cell">
              <span class="cell-label">开发天数(天)</span>
              <el-input-number v-model="node.days" :min="0.5" :max="99" :step="0.5" :precision="1" controls-position="right" style="width: 100%"/>
            </div>
            <div class="node-cell">
              <span class="cell-label">总工时(h)</span>
              <div class="cell-value">{{ calcHours(node.days) }}</div>
            </div>
            <div class="node-cell">
              <span class="cell-label">计划开始</span>
              <el-date-picker v-model="node.planStart" type="date" value-format="YYYY-MM-DD" placeholder="留空自动排期" style="width: 100%"/>
            </div>
            <div class="node-cell">
              <span class="cell-label">计划结束</span>
              <el-date-picker v-model="node.planEnd" type="date" value-format="YYYY-MM-DD" placeholder="按天数顺排" style="width: 100%"/>
            </div>
            <div class="node-cell span-2">
              <span class="cell-label">测试人员</span>
              <employee-select-multi v-model="node.testerStr" placeholder="选择测试人员（可多选）"/>
            </div>
          </div>
        </div>
        <div class="node-add">
          <page-button el-type="primary" plain @click="addNode">+ 添加节点</page-button>
        </div>
      </div>
      <template #footer>
        <page-button @click="showTaskDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveTask">保存任务</page-button>
      </template>
    </el-dialog>

    <!-- 进度填报弹窗 -->
    <el-dialog v-model="showProgressDialog" :title="'填报进度 - ' + (progressForm.taskName || '')" dialog-width="2" :close-on-click-modal="false">
      <div v-if="!progressForm.updates.length" class="progress-empty muted">该任务还没有关键节点，可先到「详情/编辑」中维护节点后填报。</div>
      <div v-for="upd in progressForm.updates" :key="upd.nodeId" class="progress-row">
        <div class="progress-node-info">
          <strong>{{ upd.nodeName }}</strong>
          <span v-if="upd.planStart || upd.planEnd" class="muted">计划时间：{{ dateOnly(upd.planStart) }} ~ {{ dateOnly(upd.planEnd) }}</span>
          <span v-if="upd.testerNames" class="muted">{{ upd.testerNames }}</span>
        </div>
        <div class="progress-node-inputs">
          <div class="progress-cell">
            <span class="cell-label">完成进度</span>
            <el-input-number v-model="upd.pct" :min="0" :max="100" :step="5" controls-position="right" style="width: 120px"/>
          </div>
          <div class="progress-cell">
            <span class="cell-label">总工时(h)</span>
            <span class="cell-value">{{ upd.hours || 0 }}</span>
          </div>
          <div class="progress-cell">
            <span class="cell-label">剩余工时(h)</span>
            <span class="cell-value">{{ calcRemain(upd) }}</span>
          </div>
          <div class="progress-cell">
            <span class="cell-label">标记完成</span>
            <el-checkbox :model-value="upd.done === 1" @change="setNodeDone(upd, $event)"/>
          </div>
        </div>
      </div>
      <form-items :data="progressForm" :columns="1" label-width="2.4rem" icon-width="0" ref="progressFormRef" style="margin-top: 8px">
        <form-item label="进度说明" :show-block="true">
          <el-input v-model="progressForm.note" type="textarea" :rows="2" placeholder="本次进度说明（可选，提交后写入进度日志）"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showProgressDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="submitProgress">提交填报</page-button>
      </template>
    </el-dialog>

    <!-- 延期原因登记弹窗（延期任务标记完成前必填） -->
    <el-dialog v-model="showDelayDialog" title="登记延期原因" dialog-width="1" :close-on-click-modal="false">
      <form-items :data="delayForm" :columns="1" label-width="2.4rem" icon-width="0" ref="delayFormRef">
        <form-item label="延期分类" :required="true" name="delayCategory" error-message="请选择延期原因分类">
          <el-select v-model="delayForm.delayCategory" placeholder="请选择延期分类" style="width: 100%">
            <el-option v-for="c in delayCategories" :key="c" :label="c" :value="c"/>
          </el-select>
        </form-item>
        <form-item label="原因说明" :show-block="true">
          <el-input v-model="delayForm.delayNote" type="textarea" :rows="3" placeholder="说明延期原因（可选）"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDelayDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="submitDelay">提交并完成</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import EmployeeSelectMulti from "@/components/h5/data/EmployeeSelectMulti.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import taskApi from "@/api/dz-project/project/taskApi";
import {TaskStatusCode} from "./projectStatus";

// 泳道列（status 为 int 码，与后端 TaskStatus 枚举一致）
const COLUMNS = [
  {key: "todo", status: TaskStatusCode.TODO, label: "未开始", color: "#909399"},
  {key: "doing", status: TaskStatusCode.DOING, label: "进行中", color: "#409EFF"},
  {key: "done", status: TaskStatusCode.DONE, label: "已完成", color: "#67C23A"},
];
// 延期原因分类
const DELAY_CATEGORIES = ["需求变更", "排期预估不足", "资源不足", "技术难点", "联调/外部阻塞", "其他"];

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "TaskBoard",
  components: {FormItems, FormItem, EmployeeSelect, EmployeeSelectMulti},
  data() {
    return {
      searchInfo: {pageNumber: 1},
      dataList: [],
      projectOptions: [],
      sprintOptions: [],
      columns: COLUMNS,
      taskStatusCodes: TaskStatusCode,
      delayCategories: DELAY_CATEGORIES,
      showTaskDialog: false,
      dialogData: {nodes: []},
      showProgressDialog: false,
      progressForm: {taskId: "", taskName: "", note: "", updates: []},
      showDelayDialog: false,
      delayRow: null,
      delayForm: {delayCategory: "", delayNote: ""},
    };
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    // 支持从项目/迭代入口带 projectId、sprintId 进入（带参进入时忽略旧缓存）
    const query = this.route.query || {};
    const hasNav = query.projectId || query.sprintId;
    if (hasNav) {
      if (query.projectId) {
        this.searchInfo.projectId = query.projectId;
      }
      if (query.sprintId) {
        this.searchInfo.sprintId = query.sprintId;
      }
      this.initOptions();
      if (query.projectId) {
        this.loadSprintOptions(query.projectId);
      }
      this.queryList();
      return;
    }
    this.initOptions();
    this.queryList();
  },
  methods: {
    async initOptions() {
      const list = await taskApi.queryProjectOptions();
      this.projectOptions = list || [];
    },
    async loadSprintOptions(projectId) {
      const list = await taskApi.querySprintOptions(projectId);
      this.sprintOptions = list || [];
    },
    colList(key) {
      const statusMap = {todo: TaskStatusCode.TODO, doing: TaskStatusCode.DOING, done: TaskStatusCode.DONE};
      return this.dataList.filter(t => t.taskStatus === statusMap[key]);
    },
    async queryList() {
      const list = await taskApi.queryBoard(this.searchInfo);
      this.dataList = list || [];
    },
    resetSearch() {
      this.queryList();
    },
    async onProjectFilter(projectId) {
      this.searchInfo.sprintId = "";
      await this.loadSprintOptions(projectId || "");
      this.resetSearch();
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    toDateValue(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    dateTime(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    onProjectChange(projectId) {
      // 编辑弹窗内切换项目后刷新批次下拉（可选填，仅更新候选）
      this.loadSprintOptions(projectId || "");
      this.dialogData.sprintId = "";
    },
    onOwnerChange(data) {
      this.dialogData.ownerName = data ? data.employeeName : "";
    },
    // ================= 任务 新建/编辑 =================
    /** 跳转任务详情页 */
    openTaskDetail(task) {
      if (task && task.id) {
        this.router.push({path: "/project/dev/task/detail", query: {id: task.id}});
      }
    },
    async openTaskDialog(task) {
      if (task) {
        // 拉详情取关键节点与测试人员
        const dto = await taskApi.detail(task.id);
        this.fillTaskDialog(dto);
      } else {
        this.fillTaskDialog({
          taskName: "", projectId: this.searchInfo.projectId || "", projectName: "",
          sprintId: this.searchInfo.projectId ? (this.searchInfo.sprintId || "") : "",
          ownerId: "", ownerName: "", estimate: null, planStart: "", planEnd: "", remark: "",
          nodes: [this.newNode()],
        });
      }
      // 加载该任务所属项目下的批次，保证批次下拉可选值正确
      this.loadSprintOptions(this.dialogData.projectId || "");
      this.showTaskDialog = true;
    },
    newNode() {
      return {nodeName: "", days: 1, planStart: "", planEnd: "", pct: 0, remain: null, done: 0, testerStr: ""};
    },
    fillTaskDialog(dto) {
      const data = JSON.parse(JSON.stringify(dto || {}));
      data.planStart = this.toDateValue(data.planStart);
      data.planEnd = this.toDateValue(data.planEnd);
      const nodes = (data.nodes || []).map(n => {
        return {
          id: n.id,
          nodeName: n.nodeName || "",
          days: n.days != null ? Number(n.days) : 1,
          planStart: this.toDateValue(n.planStart),
          planEnd: this.toDateValue(n.planEnd),
          pct: n.pct != null ? n.pct : 0,
          remain: n.remain != null ? Number(n.remain) : null,
          done: n.done || 0,
          testerStr: (n.testers || []).join(","),
        };
      });
      data.nodes = nodes.length ? nodes : [this.newNode()];
      this.dialogData = data;
    },
    addNode() {
      this.dialogData.nodes.push(this.newNode());
    },
    removeNode(idx) {
      this.dialogData.nodes.splice(idx, 1);
    },
    async saveTask() {
      if (!this.$refs.taskForm.validCheck()) {
        return;
      }
      if (this.dialogData.planStart && this.dialogData.planEnd && this.dialogData.planStart > this.dialogData.planEnd) {
        this.$message.warning("任务计划开始日期不能晚于计划完成日期");
        return;
      }
      // 仅保留填写了名称的节点；未维护节点则整体传空，避免空节点被自动排期
      const nodes = this.dialogData.nodes.filter(n => (n.nodeName || "").trim());
      for (const node of nodes) {
        if (!node.days || Number(node.days) <= 0) {
          this.$message.warning("节点[" + node.nodeName + "]的开发天数需大于0");
          return;
        }
        if (node.testerStr) {
          node.testers = node.testerStr.split(",").filter(s => s);
        } else {
          node.testers = [];
        }
        delete node.testerStr;
      }
      const payload = JSON.parse(JSON.stringify(this.dialogData));
      // 空日期置空，避免传空串导致后端日期解析失败
      payload.planStart = payload.planStart || null;
      payload.planEnd = payload.planEnd || null;
      payload.nodes = nodes.map(n => ({
        ...n,
        planStart: n.planStart || null,
        planEnd: n.planEnd || null,
      }));
      // 清掉展示冗余字段，避免上传额外键
      delete payload.testerStr;
      await taskApi.save(payload);
      this.showTaskDialog = false;
      this.resetSearch();
    },
    // ================= 状态流转 =================
    async updateTaskStatus(task, status) {
      await taskApi.updateStatus({taskId: task.id, status: status});
      this.resetSearch();
    },
    confirmDone(task) {
      // 延期中的任务必须先登记延期原因
      if (task.delayState === "延期中") {
        this.delayRow = task;
        this.delayForm = {delayCategory: "", delayNote: ""};
        this.showDelayDialog = true;
        return;
      }
      this.updateTaskStatus(task, TaskStatusCode.DONE);
    },
    async submitDelay() {
      if (!this.$refs.delayFormRef.validCheck()) {
        return;
      }
      await taskApi.updateStatus({
        taskId: this.delayRow.id,
        status: TaskStatusCode.DONE,
        delayCategory: this.delayForm.delayCategory,
        delayNote: this.delayForm.delayNote || "",
      });
      this.showDelayDialog = false;
      this.resetSearch();
    },
    // ================= 进度填报 =================
    async openProgressDialog(task) {
      const detail = await taskApi.detail(task.id);
      const updates = (detail.nodes || []).map(n => ({
        nodeId: n.id,
        nodeName: n.nodeName,
        testerNames: n.testerNames || "",
        planStart: n.planStart || "",
        planEnd: n.planEnd || "",
        pct: n.pct != null ? n.pct : 0,
        hours: n.hours != null ? Number(n.hours) : 0,
        remain: n.remain != null ? Number(n.remain) : null,
        done: n.done || 0,
      }));
      this.progressForm = {taskId: task.id, taskName: task.taskName, note: "", updates: updates};
      this.showProgressDialog = true;
    },
    /** 计划总工时(h)：开发天数 × 8h/天（四舍五入） */
    calcHours(days) {
      const d = Number(days);
      if (!d || d <= 0) {
        return 0;
      }
      return Math.round(d * 8);
    },
    /** 剩余工时(h) = 总工时 × (100 - 完成进度) ÷ 100（进度0 → 剩余=总工时） */
    calcRemain(upd) {
      if (!upd) {
        return 0;
      }
      const hours = Number(upd.hours) || 0;
      const pct = Math.min(100, Math.max(0, Number(upd.pct) || 0));
      return Math.round(hours * (100 - pct) / 100);
    },
    setNodeDone(upd, checked) {
      if (checked) {
        upd.pct = 100;
        upd.remain = 0;
        upd.done = 1;
      } else {
        upd.done = 0;
      }
    },
    async submitProgress() {
      if (!this.progressForm.updates.length) {
        this.showProgressDialog = false;
        this.$message.warning("该任务没有节点，请先在编辑中维护关键节点");
        return;
      }
      const updates = this.progressForm.updates.map(u => ({
        nodeId: u.nodeId,
        pct: u.pct,
        remain: this.calcRemain(u),
        done: u.done,
      }));
      const totalRemain = this.progressForm.updates.reduce((s, u) => s + this.calcRemain(u), 0);
      await taskApi.reportProgress({
        taskId: this.progressForm.taskId,
        note: this.progressForm.note || "",
        remain: totalRemain,
        updates: updates,
      });
      this.showProgressDialog = false;
      this.resetSearch();
    },
  },
};
</script>

<style lang="scss" scoped>
.task-board {
  height: 100%;

}
.board-body {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow-x: auto;
  gap: 10px;
  margin-bottom: 10px;
  background: #fff;
  border-radius: 4px;
  padding: 10px 8px 8px;
  box-sizing: border-box;
  .board-column {
    min-width: 300px;
    width: 33.33%;
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f5f7fa;
    border-radius: 6px;
    border: 1px solid var(--el-border-color-lighter);
    .board-column-head {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid var(--el-border-color-lighter);
      .head-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
      }
      .head-title {
        font-weight: 600;
      }
      .head-count {
        margin-left: 8px;
        background: var(--el-color-info-light-8, #f4f4f5);
        border-radius: 10px;
        padding: 0 8px;
        font-size: 12px;
        color: var(--yq-font-color-desc);
      }
    }
    .board-cards {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      .column-empty {
        text-align: center;
        color: var(--yq-font-color-desc);
        padding: 24px 0;
        font-size: 12px;
      }
      .task-card {
        background: #fff;
        border-radius: 6px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        border: 1px solid var(--el-border-color-lighter);
        padding: 8px 10px;
        margin-bottom: 8px;
        .card-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 6px;
          .card-title {
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            word-break: break-all;
            .delay-flag {
              margin-left: 6px;
              font-size: 11px;
              font-weight: normal;
              color: #f56c6c;
              background: #fef0f0;
              border-radius: 3px;
              padding: 0 4px;
              vertical-align: 1px;
            }
          }
        }
        .card-sub {
          color: var(--yq-font-color-tips);
          font-size: 12px;
          margin: 4px 0;
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .card-meta {
          color: var(--yq-font-color-desc);
          font-size: 12px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 4px;
        }
        .card-progress {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
          .el-progress {
            flex: 1;
          }
          .progress-text {
            font-size: 12px;
            color: var(--yq-font-color-desc);
          }
        }
        .card-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 4px 12px;
          margin-top: 4px;
          .done-time {
            color: var(--yq-font-color-tips);
            font-size: 12px;
          }
        }
      }
    }
  }
}
.code-text {
  font-family: Consolas, Menlo, monospace;
  font-size: 11px;
}
// 弹窗分区标题（基本信息 / 关键节点）
.form-section {
  margin-bottom: 8px;
  .form-section-title {
    font-weight: 600;
    font-size: 14px;
    position: relative;
    padding-left: 10px;
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 2px;
      bottom: 2px;
      width: 3px;
      border-radius: 2px;
      background: var(--el-color-primary);
    }
  }
}
// 节点维护
.node-section {
  margin-top: 16px;
  border-top: 1px solid var(--el-border-color-lighter);
  padding-top: 12px;
  .node-section-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 10px;
    .node-section-title {
      font-weight: 600;
      font-size: 14px;
      position: relative;
      padding-left: 10px;
      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 2px;
        bottom: 2px;
        width: 3px;
        border-radius: 2px;
        background: var(--el-color-primary);
      }
    }
    .muted {
      font-size: 12px;
    }
  }
  .node-empty {
    text-align: center;
    color: var(--yq-font-color-desc);
    font-size: 12px;
    padding: 18px 0;
    background: var(--yq-background-color-light, #fafafa);
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    margin-bottom: 10px;
  }
  .node-block {
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 8px;
    margin-bottom: 10px;
    padding: 10px 14px 14px;
    background: var(--yq-background-color-light, #fafafa);
    transition: box-shadow 0.2s;
    &:hover {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    }
    .node-block-head {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px dashed var(--el-border-color-lighter);
      .node-badge {
        flex: none;
        width: 20px;
        height: 20px;
        line-height: 20px;
        text-align: center;
        border-radius: 50%;
        background: var(--el-color-primary);
        color: #fff;
        font-size: 12px;
      }
      .node-title {
        font-size: 13px;
      }
      .flex-1 {
        flex: 1;
      }
      .node-status-tip {
        font-size: 12px;
        color: var(--el-color-primary);
        background: var(--el-color-primary-light-9);
        border-radius: 3px;
        padding: 1px 6px;
      }
    }
    .node-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px 16px;
      .node-cell {
        min-width: 0;
        &.span-2 {
          grid-column: span 2;
        }
        .cell-label {
          display: block;
          font-size: 12px;
          color: var(--yq-font-color-desc);
          margin-bottom: 4px;
        }
        .cell-value {
          display: flex;
          align-items: center;
          height: 32px;
          font-weight: 600;
          color: var(--yq-font-color-base, #303133);
        }
      }
    }
  }
  .node-add {
    display: flex;
    justify-content: center;
    margin-top: 4px;
  }
}
// 进度填报
.progress-empty {
  padding: 16px 0;
  text-align: center;
}
.progress-row {
  border-bottom: 1px dashed var(--el-border-color);
  padding: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  .progress-node-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
  }
  .progress-node-inputs {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
    .progress-cell {
      .cell-label {
        display: block;
        font-size: 12px;
        color: var(--yq-font-color-desc);
        margin-bottom: 2px;
      }
      .cell-value {
        display: inline-block;
        min-width: 60px;
        height: 30px;
        line-height: 30px;
        font-weight: 600;
        color: var(--yq-font-color-base, #303133);
      }
    }
  }
}
</style>

<style lang="scss">
/* 任务编辑弹窗 teleport 到 body，需全局样式：内容超高时弹窗内部滚动 */
.task-dialog {
  .el-dialog__body {
    max-height: calc(100vh - 220px);
    overflow-y: auto;
    padding-top: 12px;
  }
}
</style>
